import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import lodashTimes from 'lodash/fp/times';
import Checkbox, { SR_CHECKBOX_THEME_INLINE, SR_CHECKBOX_LIST_THEME_ICON_BOX } from './Checkbox';

export const SR_CHECKBOX_LIST_LAYOUT_NO_WRAP = 'noGridWrap';
export const SR_CHECKBOX_LIST_THEME_BULLET_GRID = 'bulletGrid';

class SrCheckboxList extends React.Component {
  static propTypes = {
    onChange: PropTypes.func.isRequired,
    optionList: PropTypes.array.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.array,
      PropTypes.bool,
    ]),
    multipleSelection: PropTypes.bool,
    useArrayValues: PropTypes.bool,
    allowEmptyArray: PropTypes.bool,
    toggleSingleSelection: PropTypes.bool,
    uncheckedValue: PropTypes.bool,
    fieldType: PropTypes.string,
    autoFitWidth: PropTypes.string,
    checkboxIconType: PropTypes.string,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    checkboxClassName: PropTypes.string,
    columns: PropTypes.number,
    width: PropTypes.string,
    gridGap: PropTypes.string,
    selectedItemsFirst: PropTypes.bool,
    selectedItemsOnly: PropTypes.bool,
  };

  static defaultProps = {
    value: [],
    multipleSelection: true,
    useArrayValues: false,
    allowEmptyArray: true,
    fieldType: SR_CHECKBOX_THEME_INLINE,
  };

  _onChangeCheckbox = (formValue) => {
    const {
      onChange,
      value,
      multipleSelection,
      useArrayValues,
      allowEmptyArray,
      toggleSingleSelection,
      uncheckedValue = false,
    } = this.props;

    if (!multipleSelection && !useArrayValues) {
      onChange(value === formValue && toggleSingleSelection ? uncheckedValue : formValue);
      return;
    }
    if (!multipleSelection && useArrayValues) {
      const newValue = value.includes(formValue) ? [] : [formValue];
      if (!allowEmptyArray && !newValue.length) {
        return;
      }
      onChange(newValue.sort());
      return;
    }

    const newValue = value.includes(formValue)
      ? value.filter((val) => val !== formValue)
      : [...value, formValue];

    if (!allowEmptyArray && !newValue.length) {
      return;
    }

    onChange(newValue.sort(), formValue);
  };

  render() {
    const {
      optionList,
      value,
      multipleSelection,
      useArrayValues,
      disabled,
      fieldType,
      autoFitWidth,
      checkboxIconType,
      className,
      checkboxClassName,
      columns,
      width,
      gridGap,
      selectedItemsFirst,
      selectedItemsOnly,
    } = this.props;

    let sortedOptionList = selectedItemsFirst
      ? optionList.sort((a, b) => {
          const aLabel = a.label.toLowerCase();
          const bLabel = b.label.toLowerCase();
          if (value.includes(a.value) && !value.includes(b.value)) {
            return -1;
          }

          if (value.includes(b.value) && !value.includes(a.value)) {
            return 1;
          }

          return aLabel.localeCompare(bLabel);
        })
      : optionList;

    if (selectedItemsOnly) {
      sortedOptionList = sortedOptionList.filter((option) => value.includes(option.value));
    }

    return (
      <div
        className={classnames(
          className,
          fieldType === SR_CHECKBOX_LIST_THEME_ICON_BOX && optionList.length <= 2
            ? SR_CHECKBOX_LIST_LAYOUT_NO_WRAP
            : SR_CHECKBOX_LIST_THEME_BULLET_GRID,
        )}
        style={{
          gridTemplateColumns: autoFitWidth
            ? `repeat(auto-fit, minmax(${autoFitWidth}, ${autoFitWidth}))`
            : columns
            ? lodashTimes(() => '1fr', columns).join(' ')
            : null,
          width,
          gridGap,
        }}
      >
        {sortedOptionList.map((option) => (
          <div key={option.value}>
            <Checkbox
              isActive={
                value && (multipleSelection || useArrayValues)
                  ? value.includes(option.value)
                  : value === option.value
              }
              disabled={disabled || option.disabled}
              onClick={() => {
                this._onChangeCheckbox(option.value);
              }}
              icons={
                fieldType === SR_CHECKBOX_LIST_THEME_ICON_BOX
                  ? {
                      active: option.activeIconUrl,
                      inactive: option.iconUrl,
                    }
                  : null
              }
              type={fieldType}
              checkboxIconType={checkboxIconType}
              noBackgroundImage={option.noBackgroundImage}
              className={checkboxClassName}
            >
              <span className="label">{option.label}</span>
              {option.sublabel && (
                <span className={classnames('sublabel', option.sublabelClassName)}>
                  {option.sublabel}
                </span>
              )}
            </Checkbox>
            {option.errorMessage && <div className="errorMessage">{option.errorMessage}</div>}
          </div>
        ))}
      </div>
    );
  }
}

SrCheckboxList.displayName = 'SrCheckboxList';

export default SrCheckboxList;
